import { RegistrationUserAttributes } from "@domain/authentication/auth-service";
import { isNil, isNotNil } from "@utils/assert";
import { CognitoUserAttribute } from "amazon-cognito-identity-js";

export const getRegistrationUserAttributes = (
	userAttributes: CognitoUserAttribute[],
): RegistrationUserAttributes | null => {
	const getUserAttribute = (name: string): string | undefined =>
		userAttributes.find(attribute => attribute.Name === name)?.Value;

	const locale = getUserAttribute(RegistrationCognitoAttributes.Locale);
	const countryCode = getUserAttribute(RegistrationCognitoAttributes.CountryCode);
	const agreeToPhysiomeEmails = getUserAttribute(RegistrationCognitoAttributes.AgreeToPhysiomeEmails);
	const agreeToBiogenEmails = getUserAttribute(RegistrationCognitoAttributes.AgreeToBiogenEmails);
	const touVersion = getUserAttribute(RegistrationCognitoAttributes.TouVersion);
	const pnVersion = getUserAttribute(RegistrationCognitoAttributes.PnVersion);
	const invitationId = getUserAttribute(RegistrationCognitoAttributes.InvitationId);

	if (isNil(locale)) return null;
	if (isNil(countryCode)) return null;
	if (isNil(agreeToPhysiomeEmails)) return null;
	if (isNil(agreeToBiogenEmails)) return null;
	if (isNil(touVersion)) return null;
	if (isNil(pnVersion)) return null;

	return deserializationRegistrationUserAttributes({
		locale,
		countryCode,
		agreeToBiogenEmails,
		agreeToPhysiomeEmails,
		touVersion,
		pnVersion,
		invitationId,
	});
};

export const createCognitoUserAttributes = (
	userAttributes: RegistrationUserAttributes & { email: string },
): CognitoUserAttribute[] => {
	const serializedUserAttributes = serializeRegistrationUserAttributes(userAttributes);
	const attributesList = [
		new CognitoUserAttribute({ Name: RegistrationCognitoAttributes.Email, Value: userAttributes.email }),
		new CognitoUserAttribute({ Name: RegistrationCognitoAttributes.Locale, Value: serializedUserAttributes.locale }),
		new CognitoUserAttribute({
			Name: RegistrationCognitoAttributes.CountryCode,
			Value: serializedUserAttributes.countryCode,
		}),
		new CognitoUserAttribute({
			Name: RegistrationCognitoAttributes.AgreeToPhysiomeEmails,
			Value: serializedUserAttributes.agreeToPhysiomeEmails,
		}),
		new CognitoUserAttribute({
			Name: RegistrationCognitoAttributes.AgreeToBiogenEmails,
			Value: serializedUserAttributes.agreeToBiogenEmails,
		}),
		new CognitoUserAttribute({
			Name: RegistrationCognitoAttributes.TouVersion,
			Value: serializedUserAttributes.touVersion,
		}),
		new CognitoUserAttribute({
			Name: RegistrationCognitoAttributes.PnVersion,
			Value: serializedUserAttributes.pnVersion,
		}),
	];
	if (isNotNil(serializedUserAttributes.invitationId)) {
		attributesList.push(
			new CognitoUserAttribute({
				Name: RegistrationCognitoAttributes.InvitationId,
				Value: serializedUserAttributes.invitationId,
			}),
		);
	}
	return attributesList;
};

const serializeRegistrationUserAttributes = (
	userAttributes: RegistrationUserAttributes,
): SerializeRegistrationUserAttributes => ({
	locale: userAttributes.locale.replace("-", "_"),
	countryCode: userAttributes.countryCode,
	agreeToPhysiomeEmails: userAttributes.agreeToPhysiomeEmails ? "1" : "0",
	agreeToBiogenEmails: userAttributes.agreeToBiogenEmails ? "1" : "0",
	touVersion: userAttributes.touVersion.toString(),
	pnVersion: userAttributes.pnVersion.toString(),
	invitationId: userAttributes.invitationId,
});

const deserializationRegistrationUserAttributes = (
	serializedUserAttributes: SerializeRegistrationUserAttributes,
): RegistrationUserAttributes => ({
	locale: serializedUserAttributes.locale.replace("_", "-"),
	countryCode: serializedUserAttributes.countryCode,
	agreeToPhysiomeEmails: serializedUserAttributes.agreeToPhysiomeEmails === "1",
	agreeToBiogenEmails: serializedUserAttributes.agreeToBiogenEmails === "1",
	touVersion: parseInt(serializedUserAttributes.touVersion),
	pnVersion: parseInt(serializedUserAttributes.pnVersion.toString()),
	invitationId: serializedUserAttributes.invitationId,
});

enum RegistrationCognitoAttributes {
	Email = "email",
	Locale = "locale",
	CountryCode = "custom:country_code",
	AgreeToPhysiomeEmails = "custom:physiome_emails",
	AgreeToBiogenEmails = "custom:biogen_emails",
	TouVersion = "custom:tou_version",
	PnVersion = "custom:pn_version",
	InvitationId = "custom:invitation_id",
	NewDevice = "custom:new_device",
}

type SerializeRegistrationUserAttributes = {
	[k in keyof RegistrationUserAttributes]: string;
};
